import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import {
  ChevronDown,
  ChevronUp,
  Check,
  ArrowRight,
  Clock,
  Globe,
  ChevronLeft,
  ChevronRight,
  CalendarCheck,
  Video,
  X,
} from "lucide-react";
import { FORM_QUESTIONS, COUNTRY_CODES, ASSETS } from "../mock";
import { detectCountryIso } from "../geo";
import {
  track,
  getVisitorId,
  getSessionId,
  getStoredUTM,
  getCalendlyUtm,
} from "../analytics";
import { fireRegistrationConversion, fireGoogleAdsConversion, whopTrack, PIXEL_CONFIG } from "../pixels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = "#16E36F";
const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

export const valueObj = (v) => (typeof v === "object" && v !== null ? v : {});

/* ---------- Underline text input ---------- */
export const TFInput = ({ question, value, onChange, onEnter, error }) => (
  <div className="tf-field !ml-0">
    <input
      type={question.type === "email" ? "email" : "text"}
      className="tf-input"
      placeholder={question.placeholder}
      value={value || ""}
      autoFocus={question.autoFocus !== false}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onEnter();
      }}
      data-testid={`q-input-${question.id}`}
    />
    {error && <div className="tf-error">{error}</div>}
  </div>
);

/* ---------- Phone input with country selector ---------- */
export const TFPhone = ({ question, value, onChange, onEnter, error }) => {
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(COUNTRY_CODES[0]);
  const ref = useRef(null);

  // Auto-detect the visitor's country to preselect flag + dial code.
  useEffect(() => {
    let cancelled = false;
    detectCountryIso().then((iso) => {
      if (cancelled) return;
      const match = COUNTRY_CODES.find((c) => c.code === iso);
      if (match) setCountry(match);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="tf-field !ml-0">
      <div className="flex items-end gap-3" ref={ref}>
        <div className="relative">
          <button
            type="button"
            className="tf-flag-btn"
            onClick={() => setOpen(!open)}
          >
            <span className="text-xl leading-none">{country.flag}</span>
            <ChevronDown
              size={14}
              className="transition-transform duration-200"
              style={{ color: GREEN, transform: open ? "rotate(180deg)" : "none" }}
            />
          </button>
          {open && (
            <div className="tf-dropdown-panel w-64">
              {COUNTRY_CODES.map((c) => (
                <button
                  key={c.code + c.dial}
                  type="button"
                  className="tf-option"
                  onClick={() => {
                    setCountry(c);
                    setOpen(false);
                    onChange({ ...valueObj(value), country: c.code, dial: c.dial });
                  }}
                >
                  <span className="mr-2 text-lg">{c.flag}</span>
                  <span className="flex-1 text-left">{c.name}</span>
                  <span className="opacity-60">{c.dial}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <input
          type="tel"
          className="tf-input flex-1"
          placeholder={country.example || question.placeholder}
          autoFocus={question.autoFocus !== false}
          value={valueObj(value).number || ""}
          onChange={(e) =>
            onChange({
              country: country.code,
              dial: country.dial,
              number: e.target.value,
            })
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") onEnter();
          }}
          data-testid="q-input-phone"
        />
      </div>
      {error && <div className="tf-error">{error}</div>}
    </div>
  );
};

/* ---------- Choice list (auto-advances on selection) ---------- */
const TFChoices = ({ question, value, onSelect }) => (
  <div className="tf-choices">
    {question.choices.map((choice, i) => (
      <button
        key={choice}
        type="button"
        className={`tf-choice ${value === choice ? "tf-choice-selected" : ""}`}
        onClick={() => onSelect(choice)}
        data-testid={`choice-${question.id}-${i}`}
      >
        <span className="tf-choice-letter">{LETTERS[i]}</span>
        <span className="flex-1 text-left">{choice}</span>
        {value === choice && <Check size={18} strokeWidth={3} style={{ color: GREEN }} />}
      </button>
    ))}
  </div>
);

/* ---------- Calendly-backed scheduler (visitor-timezone aware) ---------- */
export const VISITOR_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const localDateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

export const Scheduler = ({ value, onSelectSlot, submitting, refreshKey }) => {
  const [monthOffset, setMonthOffset] = useState(0);
  const [avail, setAvail] = useState({}); // { 'YYYY-MM-DD': [{iso,label}] }
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [duration, setDuration] = useState(null);
  const today = new Date();
  const viewDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthName = viewDate.toLocaleString("en-US", { month: "long" });

  const selected = valueObj(value);

  // Availability is intentionally limited to the next 3 days.
  const AVAIL_DAYS = 3;

  // Fetch the event-type duration for the header label.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/calendly/status`)
      .then((r) => {
        if (!cancelled && r.data?.duration) setDuration(r.data.duration);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch real availability (next 3 days) from Joel's Calendly.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const now = new Date();
        const start = new Date(now.getTime() + 5 * 60000);
        const end = new Date(now.getTime() + AVAIL_DAYS * 864e5);
        const res = await axios.get(`${API}/calendly/availability`, {
          params: {
            start_time: start.toISOString().replace(/\.\d{3}Z$/, "Z"),
            end_time: end.toISOString().replace(/\.\d{3}Z$/, "Z"),
          },
        });
        if (cancelled) return;
        const grouped = {};
        (res.data?.collection || []).forEach((s) => {
          if (s.status && s.status !== "available") return;
          const d = new Date(s.start_time);
          const key = localDateKey(d);
          (grouped[key] = grouped[key] || []).push({
            iso: s.start_time,
            label: d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
          });
        });
        setAvail(grouped);
      } catch (e) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [monthOffset, year, month, refreshKey]);

  const daySlots = selected.date ? avail[selected.date] || [] : [];

  return (
    <div className="scheduler">
      <div className="scheduler-header">
        <div className="flex items-center gap-3">
          <img
            src={ASSETS.typeformLogo}
            alt="The Matrix Unlocked"
            className="h-9 w-9 rounded-full object-cover"
          />
          <div>
            <div className="text-sm font-semibold text-white">
              The Matrix Unlocked
            </div>
            <div className="text-xs text-neutral-400">Enrollment Call with Joel</div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-neutral-400">
          <span className="flex items-center gap-1">
            <Clock size={13} /> {duration ? `${duration} min` : "15 min"}
          </span>
          <span className="hidden items-center gap-1 sm:flex">
            <Globe size={13} /> {VISITOR_TZ.replace(/_/g, " ")}
          </span>
        </div>
      </div>

      <div className="scheduler-body">
        <div className="scheduler-calendar">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-white">
              {monthName} {year}
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setMonthOffset(Math.max(0, monthOffset - 1))}
                className="scheduler-nav-btn"
                disabled={monthOffset === 0}
                data-testid="scheduler-prev-month"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => setMonthOffset(monthOffset + 1)}
                className="scheduler-nav-btn"
                data-testid="scheduler-next-month"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
              <div key={d} className="py-1 text-[10px] font-medium text-neutral-500">
                {d}
              </div>
            ))}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`e${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const selectable = (avail[dateStr] || []).length > 0;
              const isSelected = selected.date === dateStr;
              return (
                <button
                  key={day}
                  type="button"
                  disabled={!selectable}
                  onClick={() => onSelectSlot({ pickDate: dateStr })}
                  className={`scheduler-day ${isSelected ? "scheduler-day-selected" : ""} ${selectable ? "scheduler-day-active" : ""}`}
                  data-testid={selectable ? `scheduler-day-${day}` : undefined}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {loading && (
            <p className="mt-3 text-xs text-neutral-500">Loading available days…</p>
          )}
          {loadError && (
            <p className="mt-3 text-xs text-red-400">
              Couldn’t load availability. Please try again.
            </p>
          )}
        </div>

        <div className="scheduler-slots">
          <div className="mb-2 text-xs font-medium text-neutral-400">
            {selected.date
              ? new Date(selected.date + "T00:00:00").toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                })
              : "Select a date to see available times"}
          </div>
          {selected.date && daySlots.length === 0 && !loading && (
            <p className="text-xs text-neutral-500">No times left on this day.</p>
          )}
          {selected.date && daySlots.length > 0 && (
            <div className="scheduler-slot-wrap">
              <div className="scheduler-slot-list" data-testid="scheduler-slot-list">
                {daySlots.map((slot) => (
                  <button
                    key={slot.iso}
                    type="button"
                    disabled={submitting}
                    onClick={() =>
                      onSelectSlot({
                        date: selected.date,
                        time: slot.label,
                        start_time: slot.iso,
                        timezone: VISITOR_TZ,
                      })
                    }
                    className={`scheduler-slot ${selected.time === slot.label ? "scheduler-slot-selected" : ""}`}
                    data-testid={`scheduler-slot-${slot.label.replace(/[^0-9A-Za-z]/g, "")}`}
                  >
                    {slot.label}
                  </button>
                ))}
              </div>
              {daySlots.length > 4 && (
                <div className="scheduler-scroll-hint" aria-hidden="true">
                  <ChevronDown size={16} />
                  <span>scroll for more</span>
                </div>
              )}
            </div>
          )}
          <p className="mt-3 text-[11px] text-neutral-500">
            Times shown in your timezone ({VISITOR_TZ.replace(/_/g, " ")})
          </p>
        </div>
      </div>
    </div>
  );
};

/* ---------- Calendly inline embed (replaces the API scheduler) ---------- */
const CALENDLY_EMBED_URL =
  "https://calendly.com/d/dtd2-p3j-658/strategy-call-tmu?hide_event_type_details=1&hide_gdpr_banner=1&background_color=000000&text_color=ffffff&primary_color=16e36f";

const CalendlyEmbed = ({ name, email, onScheduled }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const params = [];
    if (name) params.push(`name=${encodeURIComponent(name)}`);
    if (email) params.push(`email=${encodeURIComponent(email)}`);
    const extra = params.join("&");
    const dataUrl = extra ? `${CALENDLY_EMBED_URL}&${extra}` : CALENDLY_EMBED_URL;

    const init = () => {
      if (window.Calendly && containerRef.current) {
        containerRef.current.innerHTML = "";
        const opts = { url: dataUrl, parentElement: containerRef.current };
        if (name || email) {
          opts.prefill = { name: name || "", email: email || "" };
        }
        const utm = getCalendlyUtm();
        if (utm) opts.utm = utm;
        window.Calendly.initInlineWidget(opts);
      }
    };

    const id = "calendly-widget-js";
    let script = document.getElementById(id);
    if (window.Calendly) {
      init();
    } else if (script) {
      script.addEventListener("load", init);
    } else {
      script = document.createElement("script");
      script.id = id;
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      script.addEventListener("load", init);
      document.body.appendChild(script);
    }
    return () => script && script.removeEventListener("load", init);
  }, [name, email]);

  useEffect(() => {
    const handler = (e) => {
      if (e.data && e.data.event === "calendly.event_scheduled") {
        onScheduled && onScheduled(e.data.payload || {});
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [onScheduled]);

  return (
    <div
      ref={containerRef}
      style={{ minWidth: "320px", height: "700px" }}
      data-testid="calendly-embed"
    />
  );
};

/* ---------- Main form: one question per slide, auto-advance ---------- */
const ApplicationForm = () => {
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [direction, setDirection] = useState("down");
  const [animKey, setAnimKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const question = FORM_QUESTIONS[qIndex];
  const total = FORM_QUESTIONS.length;
  const stepStartRef = useRef(Date.now());
  const leadIdRef = useRef(sessionStorage.getItem("tmu_lead_id") || null);
  const advanceTimer = useRef(null);
  const [, setAvailRefresh] = useState(0);
  const [bookedInfo, setBookedInfo] = useState(null);
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const submitAppFired = useRef(false);
  const startAlertFired = useRef(false);

  // Lock background scroll while the Calendly booking overlay is open, so mobile
  // users can't scroll the page behind it (which visually bugs the layout).
  // iOS Safari ignores body{overflow:hidden} for touch, so we pin the body.
  useEffect(() => {
    const overlayOpen = question.type === "scheduler" && !done && schedulerOpen;
    if (!overlayOpen) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedulerOpen, done, question]);

  useEffect(() => {
    stepStartRef.current = Date.now();
    track("form_step_view", {
      step: question.number,
      step_title: question.title,
    });
    // Whop: fire an event named after the step that just triggered
    whopTrack(question.id, {
      step: question.number,
      step_title: question.title,
    });
    // Application started: once we have first name + email, alert Discord.
    if (!startAlertFired.current && answers.full_name && answers.email) {
      startAlertFired.current = true;
      let utm = null;
      try {
        utm = JSON.parse(localStorage.getItem("tmu_utm") || "null");
      } catch (e) {}
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/application-start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: answers.full_name,
          email: answers.email,
          utm_source: (utm && utm.utm_source) || "direct",
          visitor_id: localStorage.getItem("tmu_visitor_id"),
        }),
      }).catch(() => {});
    }
    // Auto-open the Calendly overlay the moment the scheduler step is reached
    if (question.type === "scheduler") {      setSchedulerOpen(true);
      // Application is complete at this point -> fire "submit_application"
      // ONCE here (decoupled from "call_booked", which fires on booking).
      if (!submitAppFired.current) {        submitAppFired.current = true;
        const parts = String(answers.full_name || "").trim().split(/\s+/);
        const ph = valueObj(answers.phone);
        track("submit_application", { email: answers.email });
        fireRegistrationConversion({
          transactionId: answers.email ? `app_${answers.email}` : undefined,
          email: answers.email,
          firstName: parts[0] || "",
          lastName: parts.slice(1).join(" ") || "",
          phone: `${ph.dial || "+1"} ${ph.number || ""}`.trim(),
        });
        fireGoogleAdsConversion(PIXEL_CONFIG.GADS_APPLICATION_SUBMIT, {
          eventKey: "application_submit",
          email: answers.email,
          transactionId: answers.email ? `app_${answers.email}` : undefined,
        });
      }
    }
    return () => clearTimeout(advanceTimer.current);
  }, [question]);

  const stepSeconds = () =>
    Math.round((Date.now() - stepStartRef.current) / 1000);

  /* ----- progressive lead capture ----- */
  const ensureLead = async (fields) => {
    if (leadIdRef.current) return leadIdRef.current;
    try {
      const res = await axios.post(`${API}/leads`, {
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        utm: getStoredUTM(),
        ...fields,
      });
      leadIdRef.current = res.data.id;
      sessionStorage.setItem("tmu_lead_id", res.data.id);
      return res.data.id;
    } catch {
      return null;
    }
  };

  const updateLead = async (fields) => {
    try {
      if (!leadIdRef.current) {
        await ensureLead(fields);
        return;
      }
      await axios.patch(`${API}/leads/${leadIdRef.current}`, fields);
    } catch {
      // lead sync must never block the user
    }
  };

  /* ----- validation for current question ----- */
  const validate = useCallback(() => {
    const v = answers[question.id];
    let err = null;
    if (question.type === "phone_number") {
      if (!valueObj(v).number || valueObj(v).number.trim() === "")
        err = "Please fill this in";
    } else if (question.type === "scheduler") {
      const s = valueObj(v);
      if (!s.date || !s.time) err = "Please select a date and time";
    } else if (!v || (typeof v === "string" && v.trim() === "")) {
      err = "Please fill this in";
    } else if (
      question.type === "email" &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    ) {
      err = "Hmm... that email doesn't look right";
    }
    setErrors(err ? { [question.id]: err } : {});
    return !err;
  }, [question, answers]);

  const leadValueFor = (q, raw) => {
    if (q.type === "phone_number") {
      const p = valueObj(raw);
      return `${p.dial || "+1"} ${p.number || ""}`.trim();
    }
    return raw;
  };

  const goToNext = (fromQuestion, rawValue) => {
    track("form_step_complete", {
      step: fromQuestion.number,
      step_title: fromQuestion.title,
      seconds: stepSeconds(),
    });
    // progressive lead sync
    const fieldValue = leadValueFor(fromQuestion, rawValue);
    if (fromQuestion.number === 1) {
      ensureLead({ full_name: fieldValue });
    } else {
      updateLead({
        [fromQuestion.id]: fieldValue,
        last_question: fromQuestion.number,
      });
    }
    setDirection("down");
    setQIndex((i) => Math.min(i + 1, total - 1));
    setAnimKey((k) => k + 1);
  };

  /* ----- auto-advance when a text/email/phone answer becomes valid ----- */
  const autoAdvanceValid = (q, v) => {
    if (q.type === "short_text") {
      // full name: needs at least two parts ({text} space {text})
      const parts = String(v || "").trim().split(/\s+/).filter(Boolean);
      return parts.length >= 2 && parts[1].length >= 1;
    }
    if (q.type === "email") {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
    }
    if (q.type === "phone_number") {
      const digits = String(valueObj(v).number || "").replace(/\D/g, "");
      return digits.length >= 7;
    }
    return false;
  };

  const scheduleAutoAdvance = (q, v) => {
    clearTimeout(advanceTimer.current);
    if (autoAdvanceValid(q, v)) {
      advanceTimer.current = setTimeout(() => goToNext(q, v), 900);
    }
  };

  const handleTextChange = (v) => {
    setAnswers((prev) => ({ ...prev, [question.id]: v }));
    setErrors({});
    scheduleAutoAdvance(question, v);
  };

  /* ----- text/phone questions advance via OK / Enter ----- */
  const handleOk = () => {
    clearTimeout(advanceTimer.current);
    if (!validate()) {
      track("form_step_error", {
        step: question.number,
        step_title: question.title,
      });
      return;
    }
    goToNext(question, answers[question.id]);
  };

  /* ----- choice questions auto-advance on selection ----- */
  const handleChoiceSelect = (choice) => {
    setAnswers((prev) => ({ ...prev, [question.id]: choice }));
    setErrors({});
    clearTimeout(advanceTimer.current);
    advanceTimer.current = setTimeout(() => {
      goToNext(question, choice);
    }, 400);
  };

  /* ----- Calendly embed booking: submit once the invitee schedules ----- */
  const handleCalendlyScheduled = (payload) => {
    const scheduled = {
      via: "calendly_embed",
      event_uri: payload?.event?.uri,
      invitee_uri: payload?.invitee?.uri,
      timezone: VISITOR_TZ,
    };
    setAnswers((prev) => ({ ...prev, scheduled_call: scheduled }));
    submitApplication(scheduled, { skipBooking: true });
  };

  const submitApplication = async (scheduledCall, opts = {}) => {
    if (submitting || done) return;
    setSubmitting(true);
    setErrors({});
    track("form_step_complete", {
      step: 9,
      step_title: "Schedule your Enrollment Call",
      seconds: stepSeconds(),
    });
    const phone = valueObj(answers.phone);
    const phoneStr = `${phone.dial || "+1"} ${phone.number || ""}`.trim();
    const countryName =
      (COUNTRY_CODES.find((c) => c.code === phone.country) || {}).name || "";
    try {
      // 1) Book the real meeting on Joel's Calendly (blocks double-bookings,
      //    transfers all invitee data + sends the Calendly invite).
      let calendly = null;
      if (!opts.skipBooking && scheduledCall.start_time) {
        const booked = await axios.post(`${API}/calendly/book`, {
          start_time: scheduledCall.start_time,
          name: answers.full_name,
          email: answers.email,
          timezone: scheduledCall.timezone || VISITOR_TZ,
          phone: phoneStr,
          country: countryName,
          answers: {
            trading_experience: answers.trading_experience,
            trading_goals: answers.trading_goals,
            income_goal: answers.income_goal,
            portfolio_size: answers.portfolio_size,
            start_timeline: answers.start_timeline,
          },
        });
        calendly = booked.data;
        setBookedInfo({
          ...booked.data,
          date: scheduledCall.date,
          time: scheduledCall.time,
          timezone: scheduledCall.timezone || VISITOR_TZ,
        });
      }
      const scheduledWithRefs = { ...scheduledCall, calendly };

      // 2) Save the application in our own DB
      const payload = {
        full_name: answers.full_name,
        email: answers.email,
        phone: phoneStr,
        trading_experience: answers.trading_experience,
        trading_goals: answers.trading_goals,
        income_goal: answers.income_goal,
        portfolio_size: answers.portfolio_size,
        start_timeline: answers.start_timeline,
        scheduled_call: scheduledWithRefs,
        visitor_id: getVisitorId(),
        utm: getStoredUTM(),
      };
      const res = await axios.post(`${API}/applications`, payload);
      updateLead({
        scheduled_call: scheduledWithRefs,
        status: "completed",
        last_question: 9,
      });
      track("form_submit", { application_id: res.data?.id });
      // Application-funnel booking: this funnel's Whop conversion is
      // "submit_application" (fired at the scheduler step). We intentionally do
      // NOT fire the Whop "call_booked" here — that event is reserved for the
      // cold /video -> /calendar-booking -> /thankyou funnel only.
      track("application_call_booked", {
        application_id: res.data?.id,
        start_time: scheduledCall.start_time,
        timezone: scheduledCall.timezone || VISITOR_TZ,
      });
      sessionStorage.removeItem("tmu_lead_id");
      setDone(true);
    } catch (err) {
      track("form_submit_error", {});
      const status = err?.response?.status;
      const taken = status === 502 || status === 409;
      setErrors({
        scheduled_call: taken
          ? "That time was just booked by someone else — please pick another slot."
          : "Something went wrong booking your call. Please try again.",
      });
      // reset the chosen time and refresh availability so they can re-pick
      setAnswers((prev) => ({
        ...prev,
        scheduled_call: { date: scheduledCall.date, time: null },
      }));
      setAvailRefresh((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const goPrev = () => {
    if (qIndex === 0) return;
    track("form_step_back", { from_step: question.number });
    setDirection("up");
    setQIndex((i) => i - 1);
    setAnimKey((k) => k + 1);
    setErrors({});
  };

  const goNextNav = () => {
    if (qIndex >= total - 1) return;
    handleOk();
  };

  const progress = done ? 100 : (qIndex / total) * 100;
  const isTextQuestion =
    question.type === "short_text" ||
    question.type === "email" ||
    question.type === "phone_number";

  const showSchedulerOverlay =
    question.type === "scheduler" && !done && schedulerOpen;

  return (
    <>
    <div className="tf-card" id="typeform">
      {/* Progress bar */}
      <div className="tf-progress-track">
        <div
          className="tf-progress-bar"
          style={{ width: `${Math.max(progress, 2)}%` }}
        />
      </div>

      {/* Card header */}
      <div className="tf-card-header">
        <img
          src={ASSETS.typeformLogo}
          alt="TMU"
          className="h-8 w-8 rounded object-contain opacity-90"
        />
        {!done && (
          <div className="tf-step-badge" data-testid="step-badge">
            {qIndex + 1} / {total}
          </div>
        )}
      </div>

      {/* Question content */}
      <div className="tf-content">
        {done ? (
          <div className="tf-step tf-anim-down mx-auto max-w-xl text-center" data-testid="thankyou-screen">
            <h3 className="text-2xl font-semibold leading-snug text-white sm:text-3xl">
              You&rsquo;re booked in! Your enrollment call with Joel is
              confirmed.
            </h3>

            {bookedInfo && (
              <div className="tf-confirm-card" data-testid="booking-confirmation">
                <div className="tf-confirm-row">
                  <CalendarCheck size={18} className="text-[#16E36F]" />
                  <span data-testid="booking-datetime">
                    {bookedInfo.start_time
                      ? new Date(bookedInfo.start_time).toLocaleString([], {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                          hour: "numeric",
                          minute: "2-digit",
                        })
                      : `${bookedInfo.date} at ${bookedInfo.time}`}
                  </span>
                </div>
                <div className="tf-confirm-tz">
                  Your timezone ({(bookedInfo.timezone || "").replace(/_/g, " ")})
                </div>
                {bookedInfo.join_url && (
                  <a
                    href={bookedInfo.join_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="tf-join-btn"
                    data-testid="booking-join-link"
                  >
                    <Video size={16} className="mr-2" />
                    Join Zoom Call
                  </a>
                )}
              </div>
            )}

            <p className="mt-5 text-neutral-400">
              We&rsquo;ve emailed you the details. In the meantime, watch the
              following:
            </p>

            <div className="tf-summary-card" data-testid="application-summary">
              <div className="tf-summary-title">Your application</div>
              {[
                ["Trading experience", answers.trading_experience],
                ["Trading goals", answers.trading_goals],
                ["Income goal", answers.income_goal],
                ["Portfolio size", answers.portfolio_size],
                ["Start timeline", answers.start_timeline],
              ]
                .filter(([, v]) => v)
                .map(([label, v]) => (
                  <div key={label} className="tf-summary-row">
                    <span className="tf-summary-label">{label}</span>
                    <span className="tf-summary-value">{v}</span>
                  </div>
                ))}
            </div>

            <a href="#testimonials" className="tf-ok-btn mt-6 inline-flex">
              MUST WATCH VIDEOS
            </a>
          </div>
        ) : (
          <div
            key={animKey}
            className={`tf-step ${direction === "down" ? "tf-anim-down" : "tf-anim-up"} mx-auto w-full ${question.type === "scheduler" ? "max-w-3xl" : "max-w-xl"}`}
          >
            <div className="mb-1 flex items-start gap-2">
              <span className="tf-qnum">
                {question.number}
                <ArrowRight size={14} strokeWidth={3} />
              </span>
              <h3 className="tf-question-title">
                {question.title}
                <span className="tf-asterisk">*</span>
              </h3>
            </div>
            <p className="tf-question-desc !ml-[32px]">{question.description}</p>

            <div className="pl-0 sm:pl-[32px]">
              {question.type === "choices" && (
                <TFChoices
                  question={question}
                  value={answers[question.id]}
                  onSelect={handleChoiceSelect}
                />
              )}

              {question.type === "scheduler" && (
                <div className="mt-2">
                  <p className="text-base text-neutral-300">
                    Your application is complete. Pick a time below to lock in
                    your enrollment call with Joel.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSchedulerOpen(true)}
                    data-testid="open-scheduler-btn"
                    className="mt-5 inline-flex items-center justify-center rounded-lg bg-[#16E36F] px-7 py-3.5 text-lg font-bold text-black shadow-[0_6px_22px_rgba(22,227,111,0.28)] transition-transform duration-200 hover:scale-[1.03]"
                  >
                    Open booking calendar
                  </button>
                  {errors.scheduled_call && (
                    <div className="tf-error">{errors.scheduled_call}</div>
                  )}
                  {submitting && (
                    <p className="mt-3 text-sm text-neutral-400">
                      Finalizing your booking…
                    </p>
                  )}
                </div>
              )}

              {question.type === "phone_number" && (
                <TFPhone
                  question={question}
                  value={answers[question.id]}
                  onChange={handleTextChange}
                  onEnter={handleOk}
                  error={errors[question.id]}
                />
              )}

              {(question.type === "short_text" || question.type === "email") && (
                <TFInput
                  question={question}
                  value={answers[question.id]}
                  onChange={handleTextChange}
                  onEnter={handleOk}
                  error={errors[question.id]}
                />
              )}

              {isTextQuestion && (
                <div className="mt-6 flex items-center gap-3">
                  <button
                    type="button"
                    className="tf-ok-btn"
                    onClick={handleOk}
                    data-testid="ok-btn"
                  >
                    OK
                    <Check size={20} strokeWidth={3} className="ml-1.5" />
                  </button>
                  <span className="hidden text-xs text-neutral-500 sm:block">
                    press <strong className="text-neutral-300">Enter ↵</strong>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom nav arrows */}
      {!done && (
        <div className="tf-nav">
          <button
            type="button"
            className="tf-nav-btn"
            onClick={goPrev}
            disabled={qIndex === 0}
            aria-label="Previous question"
            data-testid="nav-up"
          >
            <ChevronUp size={20} />
          </button>
          <button
            type="button"
            className="tf-nav-btn"
            onClick={goNextNav}
            disabled={qIndex === total - 1}
            aria-label="Next question"
            data-testid="nav-down"
          >
            <ChevronDown size={20} />
          </button>
        </div>
      )}
    </div>

    {/* Calendly booking overlay — appears once the application is complete */}
    {showSchedulerOverlay && (
      <div
        className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/85 p-4 backdrop-blur-sm sm:p-6"
        data-testid="scheduler-overlay"
      >
        <div className="relative my-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-[#16E36F]/30 bg-[#0a0f0b] shadow-[0_20px_80px_rgba(0,0,0,0.6)]">
          <div className="relative flex items-center justify-center border-b border-white/10 px-5 py-4 sm:px-7">
            <h3 className="text-center text-lg font-bold text-white sm:text-xl">
              Book your <span className="text-[#16E36F]">enrollment call</span>
            </h3>
            <button
              type="button"
              onClick={() => setSchedulerOpen(false)}
              aria-label="Close booking calendar"
              data-testid="close-scheduler-btn"
              className="absolute right-4 top-1/2 flex h-9 w-9 flex-shrink-0 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-6"
            >
              <X size={20} />
            </button>
          </div>
          <div className="p-2 sm:p-4">
            <CalendlyEmbed
              name={answers.full_name}
              email={answers.email}
              onScheduled={handleCalendlyScheduled}
            />
          </div>
          {submitting && (
            <p className="px-7 pb-4 text-center text-sm text-[#16E36F]">
              Finalizing your booking…
            </p>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default ApplicationForm;
